/**
 * Socle front AGORA – ce que les écrans métier utilisent (ne pas importer les fichiers internes directement).
 *
 *   const { t, lib, langue } = useT();                  // libellés (dictionnaire 6 langues) – aucun texte en dur
 *   const { filiale, autorisees, changer } = useFiliale(); // filiale courante (code ou 'ALL')
 *   const droits = useDroits('service.hotline.tickets');   // { lire, creer, modifier, supprimer, valider, exporter, administrer }
 *   const { preferences, modifier } = usePreferences();    // préférences synchronisées entre appareils
 *   const api = useApi();                                  // api.get('/v1/…'), api.post(…) – erreurs ErreurApi { code }
 *   const f = useFormats();                                // f.date(d), f.montant(1200, 'FR01'), f.heure(d, 'America/Chicago')…
 *   const moi = useMoi();                                  // { email, prenom, nom, groupes, filiales, admin }
 *   const { portee, filiale } = usePortee('tiers');        // référentiel par filiale ou groupe ; <BadgePortee id="tiers" />
 *   const consolidee = useVueConsolidee();                 // 'Toutes mes filiales' : lecture seule
 *   <PiecesJointes ressource="tiers" objetId={tiers.id} /> // photos, scans (dépôt S3 direct, lignes immuables)
 *   <SelectionPiecesJointes … valeur={f} onChange={setF} /> // fichiers joints à la CRÉATION (objet en dépôt différé)
 *   <ListePiecesJointes elements={message.piecesJointes} compacte /> // pièces reçues avec les données du module
 *   <TrousseauObjet ressource="sites" objetId={site.id} filiale={site.filiale} /> // « Trousseau du site » (mots de passe)
 *
 * Formulaires (React Hook Form + schémas Zod partagés avec l'API) – voir core/formulaires/formulaire.tsx :
 *   const f = useFormulaire({ schema, valeurs, envoyer, apresEnvoi });
 *   <Formulaire formulaire={f} titre={…}> <TextField label={…} {...f.champ('nom')} /> <ActionsFormulaire formulaire={f} /> </Formulaire>
 *
 * Pages ouvertes (dock › Pages) – chaque page a son propre routeur : Link, useNavigate, useSearchParams… agissent
 * dans la page de l'écran. Un clic simple navigue dans la page ; Ctrl/⌘ + clic ouvre une nouvelle page.
 *   <LienPage to="/referentiels/tiers/42" label="Biogaz…" />  // lien + icône « Ouvrir dans une nouvelle page »
 *   const ouvrir = useOuvrir();                            // ouvrir(route, { nouvellePage, aCote, evenement })
 *   useBrouillon(formulaireModifie);                       // confirmation avant de fermer la page
 *   const quitterAgora = useQuitterAgora();                // await quitterAgora(() => window.location.assign(url))
 *   useActiviteEnCours('enregistrement', enregistre);      // confirmation avant de quitter AGORA ou fermer l'onglet
 *   useEtatPage(q ? `Recherche : ${q}` : undefined);       // résumé affiché dans le panneau Pages
 *   useTitrePage(`${tiers.code} · ${tiers.raisonSociale}`); // titre de la page (fiche d'un objet)
 *
 * Retours à l'utilisateur (zone commune montée par le socle, composants du design sur Radix UI) :
 *   const afficherMessage = useMessageEphemere();          // afficherMessage({ statut: 'succes', titre: t('…') })
 *   const confirmer = useConfirmation();                   // if (await confirmer({ titre: t('…'), danger: true })) …
 *
 * Données serveur : TanStack Query (useQuery / useMutation) avec useApi() ; mettre la filiale courante dans la clé
 * de requête pour recharger au changement de filiale. Guide complet : docs/DEVELOPPER-UN-MODULE.md.
 */
import React from 'react';
import {
  ACTIONS_NOEUD, DIMENSIONS, FILIALES, LOCALE_DE_LANGUE, TOUTES_FILIALES, filiale as trouverFiliale,
  type Action, type CatalogueReponse, type Filiale, type Moi, type NoeudCatalogue, type Portee, type Preferences,
} from '@agora/shared';
import { StatusBadge } from '../design';
import { useSocle } from '../core/contexte';
import { useT } from '../core/i18n';
import type { ClientApi } from '../core/api/client';

export { useT, type Traduire } from '../core/i18n';
export { ErreurApi, type ClientApi } from '../core/api/client';
export { useConfig } from '../core/contexte';
export { LienPage, type LienPageProps } from '../core/pages/LienPage';
export { useBrouillon, useEtatPage, useOuvrir, useQuitterAgora, useTitrePage, type OptionsOuvrir } from '../core/pages/espace';
export { useActiviteEnCours, type ActiviteEnCours } from '../core/pages/sortie';
/**
 * Ressource unique entre les onglets d'AGORA (softphone, enregistrement Scribe) : un seul onglet la détient, les
 * autres attendent et peuvent la reprendre (« Utiliser ici »).
 */
export { useVerrouOnglet, type EtatVerrou, type VerrouOnglet } from '../core/pages/verrou-onglet';
/**
 * Scribe (enregistreur de réunions monté par la coquille) : état de l'enregistrement dans cet onglet (écran Outils ›
 * Scribe : réunion en cours, à reprendre), ouverture du panneau Scribe du dock, durées affichées (« 00:42:15 »,
 * « 1 h 20 min »). Null sans droit sur outils.scribe.
 */
export { ouvrirPanneauScribe, useScribe, type ContexteScribe } from '../core/scribe/Scribe';
export { dureeLisible, formaterChrono, type PhaseScribe } from '../core/scribe/enregistreur';
/**
 * Section « Réunions » d'une fiche (Scribe, lot 2) : réunions de l'utilisateur rattachées à l'objet (menu « Affecter »
 * de la page d'une réunion) et réunions validées d'autres auteurs partagées par ce rattachement (décision R3, lecture
 * seule, auteur affiché). Masquée sans droit sur outils.scribe, et absente si l'utilisateur n'a pas activé Scribe dans
 * ses Réglages et qu'aucune réunion n'est rattachée.
 *   <ReunionsObjet ressource="tiers" objetId={tiers.id} />
 */
export { ReunionsObjet, type ReunionsObjetProps } from '../core/scribe/ReunionsObjet';
/** Fichier produit par l'API en base64 (FichierDocument : PDF d'une offre, Word d'un compte-rendu) : téléchargement. */
export { enregistrerFichier } from '../core/fichiers';
/**
 * Message éphémère (« toast », 5 s, annoncé aux lecteurs d'écran) : confirmation brève d'une action, avertissement non
 * bloquant – jamais pour une erreur de saisie (à côté du champ) ni pour une notification (dock › cloche).
 * Confirmation d'une action dans une fenêtre du design (remplace window.confirm) : résout true si l'utilisateur confirme.
 */
export { useConfirmation, useMessageEphemere, type DemandeConfirmation, type MessageEphemere } from '../core/retours/Retours';
/**
 * Vérification d'identité récente (reconnexion Microsoft avant une action sensible, 5 min par session) :
 * `const { avecVerification } = useVerificationIdentite();` puis `await avecVerification(() => api.post(…))` – rejoue
 * l'action après la reconnexion si l'API a répondu `verification_requise` ; undefined si l'utilisateur renonce.
 */
export { useVerificationIdentite } from '../core/verification/verification';
/**
 * Pièces jointes non documentaires d'un objet (photos terrain, scans, captures ; les documents gérés restent dans
 * SharePoint) : liste (droit « lire » sur `ressource`), ajout avec progression (droit « modifier », hors vue
 * consolidée), téléchargement par lien signé. Liste blanche et taille maximale contrôlées avant l'envoi ; pas de
 * suppression (lignes immuables). La ressource doit être déclarée côté API (api/src/pj/objets.ts).
 *   <PiecesJointes ressource="service.hotline.tickets" objetId={ticket.id} />
 */
export { PiecesJointes, type PiecesJointesProps } from '../core/pj/PiecesJointes';
export { TrousseauObjet, type TrousseauObjetProps } from '../core/trousseau-objet';
/** Liste de pièces jointes déjà enregistrées, avec téléchargement (compacte : fil de messages). */
export { ListePiecesJointes, type ListePiecesJointesProps } from '../core/pj/ListePiecesJointes';
/**
 * Fichiers joints à la création d'un objet déclaré en dépôt différé côté API : envoi immédiat dans un dépôt
 * temporaire, puis `piecesJointes: idsDepots(fichiers)` dans le corps de la création (bouton désactivé tant que
 * `envoiEnCours(fichiers)`). Le service du module les rattache dans sa transaction (preparerDepots, enregistrerDepots).
 */
export {
  SelectionPiecesJointes, envoiEnCours, idsDepots, type FichierJoint, type SelectionPiecesJointesProps,
} from '../core/pj/SelectionPiecesJointes';
/** Dépôt direct dans S3 d'un formulaire pré-signé (Televersement) avec progression – pièces jointes, enregistrements. */
export { deposer as deposerS3 } from '../core/pj/envoi';
/**
 * Formulaires : un seul hook pour les valeurs, la validation (schéma Zod partagé avec l'API, messages traduits), le
 * brouillon, l'envoi et les erreurs de l'API ; `Formulaire` (cadre, titre, bandeau d'erreur) et `ActionsFormulaire`
 * (Annuler / Enregistrer). Les règles de saisie restent dans packages/shared (une seule source).
 */
export {
  ActionsFormulaire, Formulaire, messageSaisie, useFormulaire,
  type ActionsFormulaireProps, type EtatFormulaire, type FormulaireProps, type OptionsFormulaire,
} from '../core/formulaires/formulaire';
/**
 * Téléphonie (POC) : appels confirmés d'un objet sur sa fiche, état du résumé, durée et correspondant d'un appel ;
 * numéro cliquable (appel en un clic par le client 3CX ou le softphone) et bouton « Rappeler » ; champ de saisie d'un
 * numéro avec le bouton téléphone du softphone.
 *   <AppelsObjet ressource="tiers" objetId={tiers.id} filiale={tiers.filiale} />
 *   <LienTelephone numero={tiers.telephone} pays={tiers.pays} />
 *   <ChampTelephone label={C('telephone')} {...f.champ('telephone')} maxLength={40} />
 */
export {
  AppelsObjet, BadgeResumeAppel, correspondantAppel, nomContactAppel, routeAppel, useDureeAppel, type AppelsObjetProps,
} from '../core/telephonie/AppelsObjet';
export {
  BoutonRappeler, ChampTelephone, LienTelephone, type ChampTelephoneProps, type LienTelephoneProps,
} from '../core/telephonie/LienTelephone';
export { SORTES_OBJETS_APPEL, routeObjetAppel } from '../core/telephonie/sortes';

/** Langue courante de l'interface (fr, en, de, it, es, pt) et locale Intl associée. */
export function useLangue() {
  const { langue, locale } = useT();
  return { langue, locale };
}

/** Utilisateur connecté (GET /v1/moi). */
export function useMoi(): Moi {
  return useSocle().moi;
}

/** Client de l'API AGORA (jeton, erreurs RFC 9457, rafraîchissement automatique). */
export function useApi(): ClientApi {
  return useSocle().api;
}

/** Filiale courante (code FR01… ou 'ALL' = toutes mes filiales), filiales autorisées et changement de filiale. */
export function useFiliale(): { filiale: string; toutes: boolean; info?: Filiale; autorisees: Filiale[]; changer: (code: string) => void } {
  const { filiale, filialesAutorisees, changerFiliale } = useSocle();
  return {
    filiale, toutes: filiale === TOUTES_FILIALES, info: trouverFiliale(filiale),
    autorisees: FILIALES.filter((f) => filialesAutorisees.includes(f.code)), changer: changerFiliale,
  };
}

/**
 * Portée d'un référentiel (décision du 22/10/2026) : `filiale` = lignes propres à chaque filiale (filtre par la
 * filiale courante) ; `groupe` = référentiel commun, qui ignore la filiale courante (`filiale` renvoyée = 'ALL').
 */
export function usePortee(idDimension: string): { portee: Portee; filiale: string } {
  const { filiale } = useSocle();
  const portee = DIMENSIONS.find((d) => d.id === idDimension)?.portee ?? 'filiale';
  return { portee, filiale: portee === 'groupe' ? TOUTES_FILIALES : filiale };
}

/**
 * Badge « Référentiel groupe » / « Référentiel par filiale » à placer dans l'en-tête d'une page de référentiel.
 * Dans une filiale précise, « par filiale » n'apporte rien : il n'apparaît qu'en vue consolidée. « Référentiel groupe »
 * reste toujours affiché (il signale des données partagées par toutes les filiales).
 */
export function BadgePortee({ id }: { id: string }) {
  const { t } = useT();
  const { portee, filiale } = usePortee(id);
  if (portee === 'filiale' && filiale !== TOUTES_FILIALES) return null;
  return React.createElement(StatusBadge, { status: portee === 'groupe' ? 'info' : 'neutral', label: t(`dim.portee.${portee}`) });
}

/** Vue consolidée « Toutes mes filiales » : lecture seule (aucune création ni modification). */
export function useVueConsolidee(): boolean {
  return !!useSocle().catalogue?.consolidee;
}

export type Droits = Record<Action, boolean>;

/** Droits de l'utilisateur sur un nœud du catalogue (métier, module, sous-module, dimension) pour la filiale courante. */
export function useDroits(idNoeud: string): Droits {
  const { noeuds } = useSocle();
  const n = noeuds.get(idNoeud);
  const d = Object.fromEntries(ACTIONS_NOEUD.map((a) => [a, !!n?.droits[a]])) as Droits;
  d.voirAccueil = !!n;
  return d;
}

/** Catalogue filtré par les droits (métiers, dimensions, pilotage) et index par id. */
export function useCatalogue(): { catalogue?: CatalogueReponse; noeud: (id: string) => NoeudCatalogue | undefined } {
  const { catalogue, noeuds } = useSocle();
  return { catalogue, noeud: (id) => noeuds.get(id) };
}

/** Préférences de l'utilisateur (langue, thème, filiale par défaut, accueil, favoris, récents…). */
export function usePreferences(): { preferences: Preferences; modifier: (maj: (p: Preferences) => Preferences) => void } {
  const { preferences, modifierPreferences } = useSocle();
  return { preferences, modifier: modifierPreferences };
}

/**
 * Formats Intl dans la langue de l'utilisateur. Devise et fuseau suivent la filiale propriétaire de l'enregistrement
 * (charte AGORA) : `montant(1250, 'US01')` → « 1 250,00 $US » pour un utilisateur francophone.
 */
export function useFormats() {
  const { langue } = useT();
  return React.useMemo(() => {
    const locale = LOCALE_DE_LANGUE[langue];
    const fuseauDe = (f?: string) => (f && f.includes('/') ? f : trouverFiliale(f || '')?.fuseau);
    return {
      locale,
      date: (d: Date | string, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }) => new Intl.DateTimeFormat(locale, opts).format(new Date(d)),
      dateHeure: (d: Date | string, filialeOuFuseau?: string) =>
        new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone: fuseauDe(filialeOuFuseau) }).format(new Date(d)),
      /** Heure dans le fuseau du site (abréviation affichée si différente du fuseau de l'utilisateur). */
      heure: (d: Date | string, filialeOuFuseau?: string) => {
        const tz = fuseauDe(filialeOuFuseau);
        const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone: tz, timeZoneName: tz && tz !== local ? 'short' : undefined }).format(new Date(d));
      },
      nombre: (n: number, opts?: Intl.NumberFormatOptions) => new Intl.NumberFormat(locale, opts).format(n),
      montant: (n: number, filialeOuDevise = 'FR01') => {
        const devise = /^[A-Z]{3}$/.test(filialeOuDevise) ? filialeOuDevise : trouverFiliale(filialeOuDevise)?.devise || 'EUR';
        return new Intl.NumberFormat(locale, { style: 'currency', currency: devise }).format(n);
      },
      /** « il y a 5 min », « hier »… */
      relatif: (d: Date | string) => {
        const s = (new Date(d).getTime() - Date.now()) / 1000;
        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        const a = Math.abs(s);
        if (a < 60) return rtf.format(Math.round(s), 'second');
        if (a < 3600) return rtf.format(Math.round(s / 60), 'minute');
        if (a < 86400) return rtf.format(Math.round(s / 3600), 'hour');
        if (a < 30 * 86400) return rtf.format(Math.round(s / 86400), 'day');
        return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(d));
      },
      /** Taille de fichier : « 850 o », « 1,2 Mo », « 20 Mo » (unités Intl de la langue). */
      taille: (octets: number) => {
        const [valeur, unite]: [number, string] = octets < 1024 ? [octets, 'byte'] : octets < 1024 ** 2 ? [octets / 1024, 'kilobyte'] : [octets / 1024 ** 2, 'megabyte'];
        return new Intl.NumberFormat(locale, { style: 'unit', unit: unite, unitDisplay: 'short', maximumFractionDigits: valeur < 10 && unite !== 'byte' ? 1 : 0 }).format(valeur);
      },
      pays: (code: string) => {
        try { return new Intl.DisplayNames([locale], { type: 'region' }).of(code) || code; } catch { return code; }
      },
    };
  }, [langue]);
}
